import axios from "axios";
import { QRCodeCanvas } from "qrcode.react";
import React, { useEffect, useMemo } from "react";
import { FaEdit, FaPlus, FaTrashAlt } from "react-icons/fa";
import { useQuery } from "react-query";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { Table } from "../../components/Table";
// import logo from "../../images/singleLogo.png";
import { GenerateRestaurantReport } from "../../components/models/GenerateRestaurantReport";
import { sortLang } from "../../utils/sort";

export const Restaurants: React.FC = () => {
  const { data, isLoading, refetch } = useQuery("Get Restaurants", async () => {
    const response = await axios.get("/restaurant");
    return response.data.restaurants.map((el: any, index: number) => ({
      id: index + 1,
      name:
        el.translations?.length === 0 ? (
          <p className="text-center">-</p>
        ) : (
          <ul className="list-disc font-semibold text-sm space-y-2">
            {sortLang(el?.translations)?.map((e: any) => (
              <li key={e.language}>
                <span className="capitalize">{e?.language}: </span>
                <span
                  className={`${
                    e.language === "ur" ? "font-urdu text-xs" : ""
                  }`}
                >
                  {e?.name || "-"}
                </span>
              </li>
            ))}
          </ul>
        ),
      logo: (
        <img
          src={process.env.REACT_APP_UPLOAD_BASE_URL + el.logo}
          className="w-10"
          alt="logo"
        />
      ),
      type: el.system_type,
      category:
        el?.category?.translations?.length === 0 ? (
          <p className="text-center">-</p>
        ) : (
          <ul className="list-disc font-semibold text-sm space-y-2">
            {sortLang(el?.category?.translations)?.map((e: any) => (
              <li key={e.language}>
                <span className="capitalize">{e?.language}: </span>
                <span
                  className={`${
                    e.language === "ur" ? "font-urdu text-xs" : ""
                  }`}
                >
                  {e?.name || "-"}
                </span>
              </li>
            ))}
          </ul>
        ),
      warrantyPeriod: el?.warrantyPeriod,
      recoveryPeriod: el?.recoveryPeriod,
      replacementPeriod: el?.replacementPeriod,
      actions: (
        <div className="flex gap-2">
          <GenerateRestaurantReport restaurantId={el.id} type="restaurant" />
          <Link to={`branches/${el.id}`}>
            <Button lightGreen>Branches</Button>
          </Link>
          <Button lightBlue onClick={() => downloadQRCode(el.Branchs)}>
            Print QR's
          </Button>
          <Link to={`/dashboard/restaurants/edit/${el.id}`}>
            <Button lightYellow className="h-10">
              <FaEdit />
            </Button>
          </Link>
          <Button lightRed onClick={() => handleDelete(el.id)}>
            <FaTrashAlt />
          </Button>
        </div>
      ),
      qr: el.Branchs.map((branch: any) => {
        return (
          <div>
            {branch?.Devices?.map((device: any) => {
              return (
                <QRCodeCanvas
                  key={device.id}
                  id={device.id}
                  className="hidden"
                  value={`{"1": "${el.id}", "2": "${device.device_id}", "3": "${
                    process.env.REACT_APP_WEB_INVOICE_URL as string
                  }?restId=${el.id}&deviceId=${device.device_id}"}`}
                  // imageSettings={imageSettings}
                  fgColor={"#000000"}
                  bgColor={"#FFFFFF"}
                  level={"H"}
                  size={255}
                  includeMargin={true}
                />
              );
            })}
          </div>
        );
      }),
    }));
  });

  // const imageSettings = {
  //   src: logo,
  //   excavate: true,
  //   height: 40,
  //   width: 40,
  // };

  const downloadQRCode = (branches: any) => {
    if (branches.length > 0) {
      // eslint-disable-next-line array-callback-return
      branches.map((branch: any) => {
        // eslint-disable-next-line array-callback-return
        branch.Devices.map((device: any) => {
          const canvas = document.getElementById(device.id) as any;
          const pngUrl = canvas
            ?.toDataURL("image/png")
            .replace("image/png", "image/octet-stream");
          let downloadLink = document.createElement("a");
          downloadLink.href = pngUrl;
          downloadLink.download = `${branch.name} - ${device.name} - ${device.device_id}.png`;
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);
        });
      });
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "#",
        accessor: "id",
      },
      {
        Header: "Logo",
        accessor: "logo",
      },
      {
        Header: "Name",
        accessor: "name",
      },
      {
        Header: "System Type",
        accessor: "type",
      },
      {
        Header: "Category",
        accessor: "category",
      },
      {
        Header: "Warranty",
        accessor: "warrantyPeriod",
        Cell: ({ row }: { row: { original: { warrantyPeriod: string } } }) => {
          const { warrantyPeriod } = row.original;
          return warrantyPeriod !== null ? (
            <p className="w-full text-center">
              {" "}
              <span className="font-semibold">{warrantyPeriod}</span> Day
            </p>
          ) : (
            <p className="w-full text-center">-</p>
          );
        },
      },
      {
        Header: "Recovery",
        accessor: "recoveryPeriod",
        Cell: ({ row }: { row: { original: { recoveryPeriod: string } } }) => {
          const { recoveryPeriod } = row.original;
          return recoveryPeriod !== null ? (
            <p className="w-full text-center">
              {" "}
              <span className="font-semibold">{recoveryPeriod}</span> Day
            </p>
          ) : (
            <p className="w-full text-center">-</p>
          );
        },
      },
      {
        Header: "Replacement",
        accessor: "replacementPeriod",
        Cell: ({
          row,
        }: {
          row: { original: { replacementPeriod: string } };
        }) => {
          const { replacementPeriod } = row.original;
          return replacementPeriod !== null ? (
            <p className="w-full text-center">
              {" "}
              <span className="font-semibold">{replacementPeriod}</span> Day
            </p>
          ) : (
            <p className="w-full text-center">-</p>
          );
        },
      },
      {
        Header: "",
        accessor: "actions",
      },
      {
        Header: "",
        accessor: "qr",
      },
    ],
    []
  );

  const handleDelete = async (id: string) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You can't reverse this deletion!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete!",
      cancelButtonText: "Cancel",
    }).then(async (result: any) => {
      if (result.isConfirmed) {
        await axios.patch(`/restaurant/delete/${id}`, {
          isDeleted: true,
        });
        refetch();
        Swal.fire({
          title: "Deleted",
          text: "The Restaurants has been removed",
          icon: "success",
          confirmButtonText: "Ok",
        });
      }
    });
  };

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading)
    return (
      <div className="flex justify-center items-center pt-32">
        <Loader />
      </div>
    );

  return (
    <div>
      <header className="my-3 flex justify-between items-center">
        <h3 className="text-2xl font-medium">All Restaurants</h3>
        <Link to="add">
          <Button className="!bg-primary flex items-center gap-2">
            <FaPlus />
            Add New
          </Button>
        </Link>
      </header>
      {!data && !isLoading && <div>Restaurants doesn't exist</div>}
      {data && <Table columns={columns} data={data} filter={true} />}
    </div>
  );
};
